import { Component, DestroyRef, ElementRef, Injector, OnDestroy, OnInit, ViewChild, computed, effect, inject, input, output, runInInjectionContext, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl } from '@angular/forms'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { ClonePersonaModalComponent } from '@app/v2/personas/clone-persona-modal/clone-persona-modal.component'
import { CreatePersonaModalComponent } from '@app/v2/personas/create-persona-modal/create-persona-modal.component'
import { ManagePersonaComponent } from '@app/v2/personas/manage-persona/manage-persona.component'
import { PersonasWizardComponent } from '@app/v2/personas/personas-wizard/personas-wizard.component'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { Persona, PersonaClonedPayload, personaResponse } from '@app/v2/personas/types/persona.type'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { MobileCollapsibleSectionComponent } from '@app/v2/shared/components/mobile-collapsible-section/mobile-collapsible-section.component'
import { MobilePersonaCardComponent } from '@app/v2/shared/components/mobile-persona-card/mobile-persona-card.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { ProgressCircleComponent } from '@app/v2/shared/components/progress-circle/progress-circle.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { AuthService } from '@auth/services/auth.service'
import { EMPTY, Subject, filter, switchMap, take, tap } from 'rxjs'
import { ArchivedItemsService } from '../../services/archived-items.service'
import { EVENT_PERSONAS_PER_PAGE, EventService } from '../../services/event.service'
import { Event } from '../../types/event.type'
import { EventPersonaCardComponent } from '../event-persona-card/event-persona-card.component'
import {
  EVENT_MOBILE_PERSONA_MENU_DROPDOWN_HEIGHTS,
  EVENT_PERSONA_DESKTOP_MENU_OPTIONS,
  EVENT_PERSONA_MOBILE_MENU_OPTIONS,
  getPersonaMenuDropdownHeight,
  withoutPersonaArchiveMenuOption
} from './event-persona-menu-options.constants'

@Component({
  selector: 'app-event-personas',
  imports: [
    MobileCollapsibleSectionComponent,
    MobilePersonaCardComponent,
    EventPersonaCardComponent,
    CreatePersonaModalComponent,
    ClonePersonaModalComponent,
    ProgressCircleComponent,
    ModalComponent
  ],
  providers: [PersonaService, ArchivedItemsService],
  templateUrl: './event-personas.component.html',
  styleUrl: './event-personas.component.scss'
})
export class EventPersonasComponent implements OnInit, OnDestroy {
  public event = input.required<Event>()
  public personas = input<Persona[]>([])
  public personasTotal = input<number | null>(null)
  public personasLoading = input(false)
  public personaPaginationEnabled = input(false)
  public createPersonaApiError = input<unknown | null>(null)
  public readonly = input(false)
  public canCreatePersona = input(true)
  public canViewPersonasList = input(true)
  public readonly createPersonaModalId = crypto.randomUUID()
  public readonly clonePersonaModalId = crypto.randomUUID()
  public isCreatingPersona = input(false)
  public readonly createPersonaSubmitted = output<string>()
  public readonly personaNameUpdate = output<Persona>()
  public readonly personaLandingPageClick = output<Persona>()
  public readonly personaViewRegistrantsClick = output<Persona>()
  public readonly personaCloned = output<PersonaClonedPayload>()
  public readonly personaArchiveClick = output<{ persona: Persona; personaIdsToArchive: number[] }>()
  /** Emitted after the legacy persona editor overlay wizard closes (e.g. to refresh v2 persona list). */
  public readonly legacyPersonaEditorClosed = output<{ event: Event }>()
  public readonly personasListingWizardClosed = output<{ event: Event }>()
  public readonly confirmArchivePersonaModalId = crypto.randomUUID()
  public readonly renamePersonaModalId = crypto.randomUUID()
  public readonly personaNameToArchive = signal('')
  public readonly personaNameToRename = signal('')
  public readonly cloneSourcePersona = signal<Persona | null>(null)
  private readonly _pendingArchivePersona = signal<Persona | null>(null)
  private readonly _pendingRenamePersona = signal<Persona | null>(null)

  public readonly BasicInputMode = BasicInputMode
  protected readonly personaMenuOptions = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canArchiveItems() ? EVENT_PERSONA_MOBILE_MENU_OPTIONS : withoutPersonaArchiveMenuOption(EVENT_PERSONA_MOBILE_MENU_OPTIONS)
  })
  protected readonly desktopPersonaMenuOptions = computed(() => {
    this._userRole()
    return this._workspaceRoleService.canArchiveItems() ? EVENT_PERSONA_DESKTOP_MENU_OPTIONS : withoutPersonaArchiveMenuOption(EVENT_PERSONA_DESKTOP_MENU_OPTIONS)
  })
  protected readonly mobilePersonaMenuDropdownHeight = computed(() => getPersonaMenuDropdownHeight(this.personaMenuOptions(), EVENT_MOBILE_PERSONA_MENU_DROPDOWN_HEIGHTS))

  public readonly mobilePersonaList = signal<Persona[]>([])
  public readonly mobileListLoading = signal(false)
  public readonly mobilePersonasTotal = signal<number | null>(null)
  public readonly mobileHasMorePersonas = signal(true)

  @ViewChild('mobileLoadMoreTrigger')
  set mobileLoadMoreTriggerRef(el: ElementRef<HTMLDivElement> | undefined) {
    this._loadMoreTriggerEl = el?.nativeElement ?? null
    this._reconnectMobileScrollObserver()
  }

  protected readonly mobileSectionTitle = computed(() => {
    if (!this.personaPaginationEnabled()) {
      return this.personasLoading() ? 'Attendee Personas' : `Attendee Personas (${this.personas().length})`
    }
    if (this.personasLoading() && !this.mobilePersonaList().length) return 'Attendee Personas'
    const total = this.mobilePersonasTotal() ?? this.personasTotal()
    if (total !== null && total !== undefined) return `Attendee Personas (${total})`
    return 'Attendee Personas'
  })

  protected readonly displayPersonasForMobile = computed(() => (this.personaPaginationEnabled() ? this.mobilePersonaList() : this.personas()))
  /** Desktop timeline: show only first 5, rest represented by a +N card. */
  protected readonly desktopVisiblePersonas = computed(() => this.personas().slice(0, this._desktopVisibleLimit))
  protected readonly desktopTotalPersonasCount = computed(() => {
    const total = this.personasTotal()
    return total ?? this.personas().length
  })

  protected readonly desktopHiddenPersonaCount = computed(() => Math.max(0, this.desktopTotalPersonasCount() - this._desktopVisibleLimit))

  protected readonly desktopPersonaSkeletonSlots = [1, 2, 3, 4, 5] as const

  protected readonly showMobilePersonasSkeleton = computed(() => {
    if (!this.personaPaginationEnabled()) return this.personasLoading()
    return (this.personasLoading() || this.mobileListLoading()) && !this.mobilePersonaList().length
  })

  protected readonly showMobilePersonasEmpty = computed(() => {
    if (!this.personaPaginationEnabled()) return !this.personasLoading() && !this.personas().length
    return !this.personasLoading() && !this.mobileListLoading() && !this.mobilePersonaList().length
  })

  private readonly _nameControls = new Map<number, FormControl<string>>()
  private readonly _load$ = new Subject<boolean>()
  private readonly _perPage = EVENT_PERSONAS_PER_PAGE
  private readonly _desktopVisibleLimit = 5
  private _page = 1
  private _mobileSeeded = false
  private _trackedEventId: number | null = null
  private _lastParentPersonaIds = new Set<number>()

  private readonly _injector = inject(Injector)
  private readonly _modalService = inject(ModalService)
  private readonly _wizardService = inject(WizardService)
  private readonly _auth = inject(AuthService)
  private readonly _personaService = inject(PersonaService)
  private readonly _archivedItemsService = inject(ArchivedItemsService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _destroyRef = inject(DestroyRef)
  private readonly _userRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null })

  private _loadMoreTriggerEl: HTMLElement | null = null
  private _observer: IntersectionObserver | null = null
  ngOnInit(): void {
    runInInjectionContext(this._injector, () => {
      this._setupMobilePersonasPagination()
      this._watchEventChangesForPagination()
      this._syncMobileListWithParentSource()
      this._syncPersonaNameControlsWithPersonas()
    })
  }

  public ngOnDestroy(): void {
    this._observer?.disconnect()
  }

  /**
   * Viewport-based IO: timeline scroll can intersect the sentinel; inner box is layout-only.
   */
  private _reconnectMobileScrollObserver(): void {
    this._observer?.disconnect()
    this._observer = null
    if (!this.personaPaginationEnabled() || !this._mobileSeeded) return
    const target = this._loadMoreTriggerEl
    if (!target) return

    this._observer = new IntersectionObserver(
      ([entry]) => {
        if (!this.personaPaginationEnabled()) return
        if (!entry.isIntersecting || !this.mobileHasMorePersonas() || this.mobileListLoading()) return
        this._load$.next(false)
      },
      { root: null, rootMargin: '200px', threshold: 0.01 }
    )
    this._observer.observe(target)
  }

  /*
   * Mobile personas load pipeline
   */
  private _setupMobilePersonasPagination(): void {
    this._load$
      .pipe(
        filter(() => this.personaPaginationEnabled()),
        tap((reset) => {
          if (reset) {
            this._page = 1
            this.mobileHasMorePersonas.set(true)
            this.mobilePersonaList.set([])
            this.mobilePersonasTotal.set(null)
          }
          this.mobileListLoading.set(true)
        }),
        switchMap(() => {
          if (!this.mobileHasMorePersonas()) return EMPTY
          const workspaceId = this.event().workspace_id
          const eventId = this.event().id
          if (!workspaceId || !eventId) return EMPTY
          // Page 1 is supplied by the timeline parent; only fetch additional pages here.
          if (this._page === 1 && this._mobileSeeded && this.mobilePersonaList().length > 0) return EMPTY
          return this._personaService.getPersonas(workspaceId, eventId, this._perPage, this._page)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (res) => this._onMobilePersonasPageLoaded(res),
        error: () => {
          this.mobileHasMorePersonas.set(false)
          this.mobileListLoading.set(false)
        }
      })
  }

  /*
   * On mobile personas page loaded
   * @param res {personaResponse} - The response
   */
  private _onMobilePersonasPageLoaded(res: personaResponse): void {
    const data = res.data ?? []
    if (res.total !== undefined && res.total !== null) {
      this.mobilePersonasTotal.set(res.total)
    }
    this.mobilePersonaList.update((curr) => (this._page === 1 ? data : [...curr, ...data]))

    const totalHint = res.total ?? this.mobilePersonasTotal() ?? this.personasTotal()
    const mergedLen = this.mobilePersonaList().length
    if (totalHint != null) this.mobileHasMorePersonas.set(mergedLen < totalHint)
    else this.mobileHasMorePersonas.set(data.length >= this._perPage)
    if (this.mobileHasMorePersonas()) this._page++
    this.mobileListLoading.set(false)
  }

  /*
   * Wire event change effect
   */
  private _watchEventChangesForPagination(): void {
    effect(() => {
      if (!this.personaPaginationEnabled()) return
      const eventId = this.event().id
      if (this._trackedEventId === eventId) return

      this._trackedEventId = eventId
      this._mobileSeeded = false
      this._lastParentPersonaIds = new Set()
      this.mobilePersonaList.set([])
      this.mobilePersonasTotal.set(null)
      this._page = 1
      this.mobileHasMorePersonas.set(true)
    })
  }

  /*
   * Wire mobile seed effect
   */
  private _syncMobileListWithParentSource(): void {
    effect(() => {
      if (!this.personaPaginationEnabled()) return
      if (this.personasLoading()) return

      const parent = this.personas()
      if (!this._mobileSeeded) {
        this._initializeMobileListFromParent(parent)
        return
      }
      this._mergeParentPersonasIntoMobileList(parent)
    })
  }

  /*
   * Seed mobile list from parent
   * @param parent {Persona[]} - The parent personas
   */
  private _initializeMobileListFromParent(parent: Persona[]): void {
    this._mobileSeeded = true
    this._lastParentPersonaIds = new Set(parent.map((p) => p.id))
    this.mobilePersonaList.set([...parent])
    if (parent.length === 0) {
      this._page = 1
      this.mobileHasMorePersonas.set(false)
      return
    }
    const total = this.personasTotal()
    if (total != null) {
      this.mobileHasMorePersonas.set(parent.length < total)
    } else {
      this.mobileHasMorePersonas.set(parent.length >= this._perPage)
    }
    this._page = parent.length >= this._perPage ? 2 : 1
    this._reconnectMobileScrollObserver()
  }

  /**
   * Merge parent into mobile
   * @param parent {Persona[]} - The parent personas
   */
  private _mergeParentPersonasIntoMobileList(parent: Persona[]): void {
    const currentParentIds = new Set(parent.map((p) => p.id))
    const removedFromParent = Array.from(this._lastParentPersonaIds).filter((id) => !currentParentIds.has(id))
    this._removePersonasFromPaginatedMobileList(removedFromParent)

    if (!parent.length) {
      this._lastParentPersonaIds = currentParentIds
      return
    }

    const parentById = new Map(parent.map((p) => [p.id, p] as const))
    this.mobilePersonaList.update((mobile) => {
      const mobileIds = new Set(mobile.map((m) => m.id))
      const additions = parent.filter((p) => !mobileIds.has(p.id))
      const updated = mobile.map((m) => {
        const p = parentById.get(m.id)
        return p ? { ...m, ...p } : m
      })
      return additions.length ? [...updated, ...additions] : updated
    })

    this._lastParentPersonaIds = currentParentIds
  }

  /**
   * Removes personas from the paginated mobile list and keeps totals / parent snapshot in sync.
   * Safe to call from confirm (same instance) and from parent merge (cross-instance via shared input).
   */
  private _removePersonasFromPaginatedMobileList(personaIds: number[]): void {
    if (!personaIds.length) return

    const removedIdSet = new Set(personaIds)
    let removedFromListCount = 0
    this.mobilePersonaList.update((mobile) =>
      mobile.filter((m) => {
        if (!removedIdSet.has(m.id)) return true
        removedFromListCount++
        return false
      })
    )

    const removedFromParentSnapshot = personaIds.filter((id) => this._lastParentPersonaIds.has(id)).length
    for (const id of personaIds) {
      this._lastParentPersonaIds.delete(id)
    }

    const totalDecrement = removedFromParentSnapshot || removedFromListCount
    if (totalDecrement > 0) {
      const currentTotal = this.mobilePersonasTotal()
      if (currentTotal != null) {
        this.mobilePersonasTotal.set(Math.max(0, currentTotal - totalDecrement))
      }
    }
  }

  /*
   * Wire persona name controls effect
   */
  private _syncPersonaNameControlsWithPersonas(): void {
    effect(() => {
      const currentPersonas = this.personas()
      const currentIds = new Set<number>()
      for (const persona of currentPersonas) {
        currentIds.add(persona.id)
        const existing = this._nameControls.get(persona.id)
        if (existing) {
          existing.setValue(persona.name ?? '', { emitEvent: false })
        } else {
          this._nameControls.set(persona.id, new FormControl(persona.name ?? '', { nonNullable: true }))
        }
      }
      for (const id of Array.from(this._nameControls.keys())) {
        if (!currentIds.has(id)) this._nameControls.delete(id)
      }
    })
  }

  /**
   * Get the form control for the persona name
   * @param persona - The persona
   * @returns {FormControl<string>} - The form control for the persona name
   */
  public getPersonaNameControl(persona: Persona): FormControl<string> {
    const control = this._nameControls.get(persona.id)
    if (control) return control
    const next = new FormControl(persona.name ?? '', { nonNullable: true })
    this._nameControls.set(persona.id, next)
    return next
  }

  /**
   * Opens the create persona modal.
   * @returns {void}
   */
  public openCreatePersonaModal(): void {
    if (this.readonly() || !this.canCreatePersona()) return
    this._modalService.toggleModal(this.createPersonaModalId)
  }

  /**
   * Cancels the create persona modal.
   * @returns {void}
   */
  public cancelCreatePersonaModal(): void {
    if (this._modalService.isModalOpen(this.createPersonaModalId)) {
      this._modalService.toggleModal(this.createPersonaModalId)
    }
  }

  /**
   * Checks if a modal is opened.
   * @param {string} modalId - The id of the modal to check
   * @returns {boolean} - Whether the modal is opened
   */
  public isModalOpened(modalId: string): boolean {
    return this._modalService.isModalOpen(modalId)
  }

  /** Forwards create-persona modal submit to the timeline parent. */
  public onCreatePersonaSubmitted(name: string): void {
    this.createPersonaSubmitted.emit(name)
    this.cancelCreatePersonaModal()
  }

  /**
   * Handles mobile inline name blur and emits commit when changed.
   * @param persona {Persona} - Persona being edited.
   */
  public handlePersonaNameBlur(persona: Persona): void {
    if (this.readonly()) return
    const control = this.getPersonaNameControl(persona)
    const trimmedValue = String(control.value ?? '').trim()
    const savedValue = String(persona.name ?? '').trim()
    if (!trimmedValue || trimmedValue === savedValue) return
    this.personaNameUpdate.emit({ id: persona.id, name: trimmedValue })
  }

  /**
   * Enter key follows blur-save flow.
   * @param event {KeyboardEvent} - Keyboard event from input.
   */
  public handlePersonaNameKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return
    event.preventDefault()
    ;(event.target as HTMLElement)?.blur()
  }

  /**
   * Emits landing page click for selected persona.
   * @param persona {Persona} - The selected persona.
   */
  public onPersonaLandingPageClick(persona: Persona): void {
    if (this.readonly()) return
    this.personaLandingPageClick.emit(persona)
  }

  /** Mobile persona card primary Manage button (same behavior as menu “Manage”). */
  /**TODO: Remove this once the form builder is done. */
  public onMobilePersonaManageClick(persona: Persona): void {
    if (this.readonly()) return
    this._openLegacyPersonaEditor(persona)
  }

  /**
   * Handles persona menu options (mobile + desktop).
   */
  public onPersonaMenuOption(persona: Persona, option: DropdownOption): void {
    if (this.readonly()) return
    if (option.key === 'manage') {
      this._openLegacyPersonaEditor(persona)
      return
    }
    if (option.key === 'landing_page') {
      this.onPersonaLandingPageClick(persona)
      return
    }
    if (option.key === 'view_registrants') {
      this.personaViewRegistrantsClick.emit(persona)
      return
    }
    if (option.key === 'rename') {
      this.openRenamePersonaModal(persona)
      return
    }
    if (option.key === 'clone') {
      this.openClonePersonaModal(persona)
      return
    }
    if (option.key === 'archive') {
      this.onArchivePersona(persona)
    }
  }

  /**
   * Opens the rename persona modal.
   * @param persona {Persona} - The selected persona.
   */
  public openRenamePersonaModal(persona: Persona): void {
    if (this.readonly()) return
    this._pendingRenamePersona.set(persona)
    this.personaNameToRename.set(persona.name)
    this._modalService.toggleModal(this.renamePersonaModalId)
  }

  /**
   * Opens the clone persona modal for the selected persona.
   * @param persona {Persona} - The selected persona.
   */
  public openClonePersonaModal(persona: Persona): void {
    if (this.readonly()) return
    this.cloneSourcePersona.set(persona)
    this._modalService.toggleModal(this.clonePersonaModalId)
  }

  /**
   * Closes the clone persona modal.
   */
  public closeClonePersonaModal(): void {
    if (this._modalService.isModalOpen(this.clonePersonaModalId)) {
      this._modalService.toggleModal(this.clonePersonaModalId)
    }
    this.cloneSourcePersona.set(null)
  }

  /**
   * Forwards a successful clone to the timeline.
   * @param result {PersonaClonedPayload} - Cloned persona and API message
   */
  public onPersonaCloned(result: PersonaClonedPayload): void {
    this.personaCloned.emit(result)
    this.closeClonePersonaModal()
  }

  /**
   * Cancels the rename persona modal.
   * @returns {void}
   */
  public cancelRenamePersonaModal(): void {
    if (this._modalService.isModalOpen(this.renamePersonaModalId)) {
      this._modalService.toggleModal(this.renamePersonaModalId)
    }
    this._pendingRenamePersona.set(null)
    this.personaNameToRename.set('')
  }

  /**
   * Handles rename persona submitted.
   * @param name {string} - The new name for the persona.
   */
  public onRenamePersonaSubmitted(name: string): void {
    const target = this._pendingRenamePersona()
    if (!target) return
    this.personaNameUpdate.emit({ id: target.id, name })
    this.cancelRenamePersonaModal()
  }

  /**
   * Handles archive persona click.
   * @param persona {Persona} - The selected persona.
   */
  public onArchivePersona(persona: Persona): void {
    this._pendingArchivePersona.set(persona)
    this.personaNameToArchive.set(persona.name)
    this._modalService.toggleModal(this.confirmArchivePersonaModalId)
  }

  /**
   * Cancels the archive persona modal.
   * @returns {void}
   */
  public cancelArchivePersona(): void {
    if (this._modalService.isModalOpen(this.confirmArchivePersonaModalId)) {
      this._modalService.toggleModal(this.confirmArchivePersonaModalId)
    }
    this._pendingArchivePersona.set(null)
    this.personaNameToArchive.set('')
  }

  /**
   * Confirms the archive persona.
   * @returns {void}
   */
  public confirmArchivePersona(): void {
    if (this._modalService.isModalOpen(this.confirmArchivePersonaModalId)) {
      this._modalService.toggleModal(this.confirmArchivePersonaModalId)
    }
    const persona = this._pendingArchivePersona()
    if (!persona) return

    const personaIdsToArchive = this.personaPaginationEnabled() ? [persona.id] : this._archivedItemsService.collectLinkedPersonaIdsForRemoval(persona, this.personas())

    if (this.personaPaginationEnabled()) {
      this._removePersonasFromPaginatedMobileList(personaIdsToArchive)
    }
    personaIdsToArchive.forEach((id) => this._nameControls.delete(id))
    this._pendingArchivePersona.set(null)
    this.personaNameToArchive.set('')

    this.personaArchiveClick.emit({ persona, personaIdsToArchive: personaIdsToArchive })
  }

  /**
   * Navigate to the personas listing page
   */
  public navigateToPersonasWizard(): void {
    if (this.readonly()) return
    const wizard = this._wizardService.open<PersonasWizardComponent>({
      component: PersonasWizardComponent,
      componentInputs: {
        eventId: this.event().id,
        organizationId: this._auth.currentWorkspace?.id,
        eventSlug: this.event().slug,
        eventName: this.event().name
      },
      headerConfig: {
        breadcrumbs: [this.event().name, 'Attendee Personas']
      }
    })
    wizard.onClose.pipe(take(1), takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      if (wizard.componentRef.instance.hasPersonaListMutations()) {
        this.personasListingWizardClosed.emit({ event: this.event() })
      }
    })
  }

  /**
   * Opens Manage Persona form-builder shell; Next opens legacy editor.
   */
  private _openLegacyPersonaEditor(persona: Persona): void {
    if (this.readonly()) return
    const ev = this.event()
    const { onClose } = this._wizardService.open({
      component: ManagePersonaComponent,
      componentInputs: {
        eventId: ev.id,
        organizationId: this._auth.currentWorkspace?.id ?? ev.workspace_id,
        personaId: persona.id,
        eventName: ev.name,
        personaName: persona.name
      },
      headerConfig: {
        breadcrumbs: [ev.name, persona.name]
      }
    })
    onClose.pipe(take(1)).subscribe(() => this.legacyPersonaEditorClosed.emit({ event: ev }))
  }
}
